// src/merch/products.js
//
// Product packs. Each product defines its sizes, inventory colours, and
// the printable area(s) the customer designs on.
//
// PRINT SPACE: every design is composited on a square 2048px canvas (the
// shared engine's UV space). `printArea` is the rectangle of that square
// (normalised 0–1) that maps onto the physical sticker / dieline. Keeping
// it in the middle of the square with the dieline's true aspect ratio
// means the flat export drops straight onto the dieline.
//
// Sticks use a real .glb (model.glb) with zones extracted from it; the puck
// is still placeholder geometry until its model lands.

// The sticker occupies AREA_FILL of the square's long axis, leaving room
// around it for bleed (artwork that runs past the cut line so there are
// no slivers of bare vinyl if the cut drifts slightly).
const AREA_FILL = 0.9;

function centredArea(widthMm, heightMm) {
  const aspect = widthMm / heightMm;
  if (aspect >= 1) {
    const w = AREA_FILL;
    const h = w / aspect;
    return { x0: 0.5 - w / 2, x1: 0.5 + w / 2, y0: 0.5 - h / 2, y1: 0.5 + h / 2 };
  }
  const h = AREA_FILL;
  const w = h * aspect;
  return { x0: 0.5 - w / 2, x1: 0.5 + w / 2, y0: 0.5 - h / 2, y1: 0.5 + h / 2 };
}

export { cornerRadiusMm } from "./shapes";

export const BLEED_MM = 2;

// Stick print zones come from the real 3D model (see
// tools/merch/extract_zones.py): true sticker sizes + cut outlines.
import stick27 from "./models/stick-27.zones.json";
import ornament from "./models/ornament.zones.json";
import { GUIDES, ORNAMENT_FACE } from "./guides";

export const PRODUCTS = [
  {
    id: "mini-stick",
    name: "Mini Stick",
    blurb: "Custom shaft and blade decals for a mini hockey stick.",
    model: { glb: stick27.model },
    handed: true, // Left / Right shot (stickers + model mirror for lefties)
    sizes: [
      { id: "27", label: '27"', zones: stick27.zones },
      // TODO: confirm with Jon whether 30" = same blade, longer shaft; then
      // re-run extract_zones.py on a stick-30.glb (or stretch the shaft).
      { id: "30", label: '30"', zones: stick27.zones, comingSoon: true },
    ],
    colours: [
      { id: "black", label: "Black", hex: "#1b1b1d", inStock: true },
      { id: "white", label: "White", hex: "#f2f2ef", inStock: true },
      { id: "red", label: "Red", hex: "#b3242b", inStock: true },
      { id: "navy", label: "Navy", hex: "#1c2a4a", inStock: true },
      { id: "gold", label: "Gold", hex: "#c9a227", inStock: false },
    ],
    price: 24.99,
    backSidePrice: 0, // TODO: agree with Jon; server: MERCH_BACK_SIDE_CENTS
  },
  {
    id: "ornament",
    name: "Ornament",
    blurb: "A player ornament — draw it, colour it, or add your face.",
    model: { glb: ornament.model },
    sizes: [{ id: "std", label: "Standard", zones: ornament.zones }],
    // where an uploaded face lands (fractions of the sticker): the helmet
    // area on the front — see FaceDialog
    faceAnchor: { "ornament-front": ORNAMENT_FACE },
    // recolourable player art (jersey.js; assets from tools/merch/prep_template.py)
    template: { base: "/merch/templates/ornament/" },
    sheetName: "Hockey Player", // colouring-sheet header: "ORNAMENT: / Hockey Player: FRONT"
    colours: [
      { id: "natural", label: "Natural wood", hex: "#d8b88a", inStock: true },
      { id: "white", label: "White", hex: "#f2f2ef", inStock: true },
      { id: "black", label: "Black", hex: "#1b1b1d", inStock: true },
    ],
    price: 14.99, // TODO: confirm with Jon (supplier pricing)
  },
  {
    id: "sticker",
    name: "Sticker",
    blurb: "Die-cut sticker: cut to the shape of your design with a smooth border.",
    model: { type: "sticker" },
    // contour: the cut line is worked out from the artwork (contour.js);
    // the size is the biggest it can be (the box the design fits in)
    sizes: [
      { id: "2", label: '2"', sticker: { widthMm: 50.8, heightMm: 50.8, contour: true } },
      { id: "3", label: '3"', sticker: { widthMm: 76.2, heightMm: 76.2, contour: true } },
      { id: "4", label: '4"', sticker: { widthMm: 101.6, heightMm: 101.6, contour: true } },
    ],
    colours: [
      { id: "white", label: "White vinyl", hex: "#ffffff", inStock: true },
      { id: "clear", label: "Clear vinyl", hex: "#eef2f4", inStock: true },
    ],
    price: 5.99, // TODO: real prices per size (server catalog)
  },
  {
    id: "puck",
    name: "Puck",
    blurb: "Full-colour design on a regulation puck face.",
    model: { type: "puck" },
    sizes: [{ id: "reg", label: 'Regulation (3")', scale: 1.0, sticker: { widthMm: 76, heightMm: 76, round: true } }],
    colours: [{ id: "black", label: "Black", hex: "#141414", inStock: true }],
    price: 14.99,
  },
];

/**
 * Printable zones ("sides") of a product in a size. Each zone carries its
 * own sticker { widthMm, heightMm, outline?, round? }. Model-based
 * products (sticks) have shaft/blade × front/back zones; simple products
 * have one "front" zone using size.sticker.
 */
export function getSides(product, size, hand = "right") {
  size = size || product.sizes[0];
  if (size.zones?.length) {
    // Left-handed = the mirror image of the right-handed stick we model:
    // every sticker outline flips horizontally (and the 3D model mirrors).
    const left = hand === "left" && product.handed;
    return size.zones.map((z) => ({
      id: z.id,
      label: z.label,
      part: z.part,
      face: z.face,
      pair: z.pair,
      mesh: z.mesh,
      uv: z.uv,
      mirror: left,
      // colouring guides (non-printing), mirrored for lefties like the outline
      guides: (GUIDES[z.id] || []).map((g) => (left ? { ...g, pts: g.pts.map(([x, y]) => [z.widthMm - x, y]) } : g)),
      sticker: {
        widthMm: z.widthMm,
        heightMm: z.heightMm,
        outline: left ? z.outline.map(([x, y]) => [+(z.widthMm - x).toFixed(2), y]).reverse() : z.outline,
      },
    }));
  }
  const list = product.sides?.length ? product.sides : [{ id: "front", label: "Front" }];
  return list.map((sd) => ({ ...sd, sticker: size.sticker }));
}

export function getZone(product, size, id, hand = "right") {
  const zs = getSides(product, size, hand);
  return zs.find((z) => z.id === id) || zs[0];
}

/** Print resolution for a sticker: long strips get a bigger canvas so
 *  they don't print soft (~140 dpi cap to keep memory sane on iPads). */
export function printSizeFor(sticker) {
  const longMm = Math.max(sticker.widthMm, sticker.heightMm);
  return Math.min(3072, Math.max(2048, Math.round((longMm * 5.6) / 256) * 256));
}

export function getProduct(id) {
  return PRODUCTS.find((p) => p.id === id) || PRODUCTS[0];
}

export function getSize(product, sizeId) {
  return product.sizes.find((s) => s.id === sizeId) || product.sizes[0];
}

export function getColour(product, colourId) {
  const inStock = product.colours.filter((c) => c.inStock);
  return inStock.find((c) => c.id === colourId) || inStock[0] || product.colours[0];
}

/** Where a sticker sits in the 2048 design square (accepts a sticker or
 *  a legacy size object). */
export function printAreaFor(stickerOrSize) {
  const st = stickerOrSize?.sticker || stickerOrSize;
  return centredArea(st.widthMm, st.heightMm);
}
